// Figma frame 04 invited (33:1411) -> /auth/invited. PRD 18 US-090.
//
// There is no backend endpoint that resolves an invite by token
// (`backend/auth/store.py::pending_invite` is looked up by email only, at
// `/auth/consume` time — see this agent's progress notes, "Deviation to
// disclose"). So this screen's org/email/inviter context comes entirely
// from the URL query string an admin's invite link is expected to carry
// (`?email=&org=`), not from a fetch. "Accepting" an invite means asking
// the backend to email that address a normal sign-in link
// (`POST /auth/request`, US-021) — the invite itself is redeemed later,
// when that link is clicked and consumed.

import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button, Field, Icon, Input } from "../../ui";
import { AuthShell } from "./AuthShell";
import { requestSignInLink } from "./api";

type InviteParams = { email: string; org: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

function parseInviteParams(search: string): InviteParams | null {
  const params = new URLSearchParams(search);
  const email = params.get("email")?.trim() ?? "";
  const org = params.get("org")?.trim() ?? "";
  if (!email || !org) return null; // Covers "no invite token in the URL".
  if (!EMAIL_RE.test(email) || !SLUG_RE.test(org)) return null; // "invalid".
  return { email, org };
}

/** "acme-docs" -> "AD", mirroring Primitive/Avatar's initials variant. */
function orgInitials(orgSlug: string): string {
  const parts = orgSlug.split(/[-_\s]+/).filter(Boolean);
  const initials = parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
  return initials || orgSlug.slice(0, 2).toUpperCase();
}

function InvalidInvite() {
  const navigate = useNavigate();
  return (
    <AuthShell>
      <span className="maisight-invite-status-icon" aria-hidden="true">
        <Icon name="alert-triangle" size={28} />
      </span>
      <h1 className="maisight-invite-heading type-h2">This invite link isn&apos;t valid</h1>
      <p className="maisight-invite-body type-bodyS">
        It may be missing its details, already used, or no longer active. Ask
        whoever invited you to send a fresh one, or sign in if you already
        have an account.
      </p>
      <div className="maisight-invite-form">
        <Button
          variant="primary"
          block
          onClick={() => navigate({ to: "/signin" })}
        >
          Back to sign in
        </Button>
      </div>
    </AuthShell>
  );
}

export function InvitedScreen() {
  const navigate = useNavigate();
  // Read once: this screen never re-derives the invite from a later URL
  // change, and the token/params are never written to storage.
  const invite = useMemo(
    () => parseInviteParams(window.location.search),
    [],
  );
  const [status, setStatus] = useState<"idle" | "sending" | "error">("idle");

  if (!invite) return <InvalidInvite />;

  const { email, org } = invite;

  async function handleAccept() {
    setStatus("sending");
    try {
      await requestSignInLink(email);
      navigate({ to: "/auth/link-sent" });
    } catch {
      setStatus("error");
    }
  }

  return (
    <AuthShell>
      <div className="maisight-invite-org">
        <span className="maisight-invite-org-avatar type-numSm">
          {orgInitials(org)}
        </span>
        <span className="maisight-invite-org-name type-label">{org}</span>
      </div>
      <h1 className="maisight-invite-heading type-h2">Join {org}</h1>
      <p className="maisight-invite-body type-bodyS">
        {`You've been invited to the ${org} workspace on mAiSight.`}
      </p>
      <div className="maisight-invite-form">
        <Field label="Work email" help="The invite was sent to this address.">
          <Input value={email} readOnly />
        </Field>
        <Button
          type="button"
          variant="primary"
          block
          onClick={handleAccept}
          disabled={status === "sending"}
        >
          {status === "sending" ? "Sending…" : "Accept invite"}
        </Button>
        {status === "error" ? (
          <p className="maisight-invite-body type-bodyS" role="alert">
            {`Couldn't send your link — check your connection and try again.`}
          </p>
        ) : null}
        <p className="maisight-invite-footer type-bodyS">
          {`You'll be added as a member. Admins can change that later.`}
        </p>
      </div>
    </AuthShell>
  );
}
